// Candy Rack displays quantity changes before its debounced cart request finishes.
// Keep checkout on the page until the server agrees with the displayed cart.
export function cartQuantitiesMatch(expected, items) {
  const actual = new Map();
  for (const item of items) {
    const id = String(item.variant_id);
    actual.set(id, (actual.get(id) || 0) + item.quantity);
  }
  return expected.size === actual.size && [...expected].every(([id, quantity]) => actual.get(id) === quantity);
}

export function trackCartRequests(scope) {
  const original = scope.fetch;
  const state = { pending: 0, revision: 0 };
  scope.fetch = function (input, init) {
    let mutation = false;
    try {
      const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, scope.location.href);
      const method = String(init?.method || input?.method || 'GET').toUpperCase();
      mutation = url.origin === scope.location.origin && method !== 'GET' && method !== 'HEAD' &&
        /\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname);
    } catch { /* Preserve fetch's own handling of invalid input. */ }
    if (!mutation) return original.apply(this, arguments);
    state.pending++;
    state.revision++;
    try {
      return original.apply(this, arguments).finally(() => { state.pending--; });
    } catch (error) {
      state.pending--;
      throw error;
    }
  };
  return state;
}

export async function waitForCartCheckout({
  requests, displayedQuantities, readCart, refreshCart, lastEdit, canContinue,
  now = Date.now, pause = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds)),
}) {
  const deadline = now() + 10000;
  let refreshedAt = null;

  while (now() < deadline) {
    if (!canContinue()) return 'cancelled';
    if (requests.pending || now() - lastEdit() < 800) {
      refreshedAt = null;
      await pause(100);
      continue;
    }

    const revision = requests.revision;
    const edit = lastEdit();
    const expected = displayedQuantities();
    const cart = await readCart();
    if (!canContinue()) return 'cancelled';
    if (requests.pending || revision !== requests.revision || edit !== lastEdit()) {
      refreshedAt = null;
      await pause(100);
      continue;
    }

    const current = displayedQuantities();
    if (expected && current && cartQuantitiesMatch(expected, cart.items) && cartQuantitiesMatch(current, cart.items)) {
      return 'checkout';
    }

    if (refreshedAt === null) {
      refreshedAt = now();
      if (!refreshCart()) return 'review';
    } else if (now() - refreshedAt >= 1500) {
      return 'review';
    }
    await pause(200);
  }
  throw new Error('Cart verification timed out');
}

function installCheckoutGuard() {
  const requests = trackCartRequests(window);
  const checkoutSelector = '.candyrack-slider-cart-footer-checkout-button';
  const quantitySelector = '.candyrack-slider-cart-item-product-details-quantity-input';
  const controlSelector = `${quantitySelector},.candyrack-slider-cart-item-product-details-quantity-button-plus,.candyrack-slider-cart-item-product-details-quantity-button-minus,.candyrack-slider-cart-item-product-details-remove-button`;
  const config = JSON.parse(document.getElementById('bape-candyrack-cart-config')?.textContent || '{}');
  const cartPath = `${window.Shopify?.routes?.root || '/'}cart`;
  const cartUrl = new URL(cartPath, window.location.href);
  const pageUrl = new URL(window.location.href);
  if (pageUrl.pathname === cartUrl.pathname && pageUrl.searchParams.get('cart_recovered') === '1') {
    const heading = document.querySelector('#MainContent h1');
    if (heading) {
      const notice = document.createElement('p');
      notice.setAttribute('role', 'status');
      notice.textContent = config.checkoutRecovered || 'Your cart has been refreshed. Review your items, then continue to checkout.';
      heading.after(notice);
      pageUrl.searchParams.delete('cart_recovered');
      window.history.replaceState(window.history.state, '', pageUrl);
    }
  }
  let lastEdit = 0;
  let waiting = false;
  const approvedClicks = new WeakSet();
  const currentButton = () => document.querySelector(checkoutSelector);
  const reviewCart = () => {
    cartUrl.searchParams.set('cart_recovered', '1');
    window.location.assign(cartUrl.href);
  };

  const displayedQuantities = () => {
    const quantities = new Map();
    for (const row of document.querySelectorAll('#candyrack-slider-cart .candyrack-slider-cart-item-product-details-container')) {
      const link = row.querySelector('a[href]');
      const quantity = Number(row.querySelector(quantitySelector)?.value);
      const id = link && new URL(link.href).searchParams.get('variant');
      if (!id || !Number.isInteger(quantity) || quantity < 0) return null;
      if (quantity) quantities.set(id, (quantities.get(id) || 0) + quantity);
    }
    return quantities;
  };

  for (const eventName of ['click', 'input', 'change']) {
    document.addEventListener(eventName, event => {
      if (event.target instanceof Element && event.target.closest(controlSelector)) lastEdit = Date.now();
    }, true);
  }

  document.addEventListener('click', async event => {
    const button = event.target instanceof Element && event.target.closest(checkoutSelector);
    if (!(button instanceof HTMLButtonElement)) return;
    if (approvedClicks.delete(button)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (waiting) return;
    waiting = true;
    button.setAttribute('aria-busy', 'true');
    let status = document.getElementById('bape-cart-checkout-status');
    if (!status) {
      status = document.createElement('p');
      status.id = 'bape-cart-checkout-status';
      status.setAttribute('role', 'status');
      button.before(status);
    }
    status.textContent = config.checkoutUpdating || 'Updating your cart…';
    const syncStatus = () => {
      // The app may replace its footer while refreshing the cart.
      if (!status.isConnected) currentButton()?.before(status);
      return status;
    };
    try {
      const outcome = await waitForCartCheckout({
        requests,
        displayedQuantities,
        lastEdit: () => lastEdit,
        canContinue: () => {
          const checkout = currentButton();
          return checkout?.isConnected && getComputedStyle(checkout).visibility !== 'hidden';
        },
        readCart: async () => {
          syncStatus();
          const response = await fetch(`${cartPath}.js`, {
            cache: 'no-store', signal: AbortSignal.timeout(5000),
          });
          if (!response.ok) throw new Error('Cart verification failed');
          return response.json();
        },
        refreshCart: () => {
          if (typeof window.CandyRackSliderCart?.openCart !== 'function') return false;
          syncStatus().textContent = config.checkoutRefreshing || 'Refreshing your cart…';
          window.CandyRackSliderCart.openCart();
          return true;
        },
      });
      status.textContent = '';
      if (outcome === 'review') {
        reviewCart();
      } else if (outcome === 'checkout') {
        const checkout = currentButton();
        if (!(checkout instanceof HTMLButtonElement)) return;
        checkout.removeAttribute('aria-busy');
        approvedClicks.add(checkout);
        checkout.click();
      }
    } catch {
      syncStatus().textContent = config.checkoutRetry || 'We could not confirm your cart. Check your connection and try again, or review your cart.';
      // Recovery must remain usable even when the app's save never settles.
      // This opens a fresh server-rendered cart for review; it does not
      // authorize checkout or replay an uncertain cart mutation.
      const review = document.createElement('button');
      review.type = 'button';
      review.textContent = config.checkoutReview || 'Review cart';
      review.addEventListener('click', reviewCart);
      status.append(' ', review);
    } finally {
      waiting = false;
      button.removeAttribute('aria-busy');
      currentButton()?.removeAttribute('aria-busy');
    }
  }, true);
}

if (typeof window !== 'undefined') installCheckoutGuard();
